import { useState } from 'react'
import { TableShell } from '../../components/molecules/TableShell'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { ProductReceiveModal } from './ProductReceiveModal'
import {
  useWarehouseProductPurchases,
  type ProductAggregateDto
} from '../../../viewmodels/useWarehouseProductPurchases'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /warehouse/product-purchases — per-product summary with the
 * Receive flow that allocates a slice of a bare Manage Purchase onto
 * a product (writes a wpp allocation row + variant stock movements
 * atomically). The per-buy ledger was dropped 2026-06-04; the
 * canonical supplier-invoice ledger lives on Manage > Purchases and
 * the stock-in audit trail on the two stock-history screens.
 */
export function ProductPurchasesPage(): JSX.Element {
  const vm = useWarehouseProductPurchases()
  const { can } = useAuth()
  const [receiveProduct, setReceiveProduct] = useState<ProductAggregateDto | null>(null)

  const canReceive = can(SCREENCODES.WAREHOUSEPRODUCTPURCHASES, 'edit')

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-heading text-h2 text-charcoal">Product Purchases</h1>
        <p className="font-body text-body-sm text-text-secondary mt-1">
          One row per product. Receive allocates a Manage Purchase's qty across variants
          and lands it as stock.
        </p>
      </header>

      {vm.isAggregatesLoading ? (
        <div className="flex items-center justify-center py-8"><Spinner /></div>
      ) : vm.productAggregates.length === 0 ? (
        <Placeholder>No products yet — add one on Products first.</Placeholder>
      ) : (
        <ProductSummaryTable
          items={vm.productAggregates}
          canReceive={canReceive}
          onReceive={(row) => setReceiveProduct(row)}
        />
      )}

      <ProductReceiveModal
        open={receiveProduct !== null}
        product={receiveProduct}
        onClose={() => {
          setReceiveProduct(null)
          vm.reloadAggregates()
        }}
        getOptions={vm.getReceiveOptionsByProduct}
        onAllocate={vm.allocate}
      />
    </div>
  )
}

function Placeholder({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="text-center py-10 border border-border rounded-md bg-parchment/40">
      <p className="font-body text-body-sm text-text-secondary">{children}</p>
    </div>
  )
}

function ProductSummaryTable({
  items,
  canReceive,
  onReceive
}: {
  readonly items: ReadonlyArray<ProductAggregateDto>
  readonly canReceive: boolean
  readonly onReceive: (row: ProductAggregateDto) => void
}): JSX.Element {
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Product</Th>
            <Th className="text-right">In stock</Th>
            <Th className="text-right">Pending invoices</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const receiveDisabled = !canReceive || !row.link_active
            const title = !canReceive
              ? ''
              : !row.link_active
                ? 'No supplier linked to this product on Manage > Product Suppliers.'
                : ''
            const label = row.pending_invoice_count === 0 ? 'View' : 'Receive'
            return (
              <tr key={row.product_id} className="border-t border-border">
                <Td>{row.product_name}</Td>
                <Td className="text-right whitespace-nowrap font-mono">{row.in_stock_units.toLocaleString('en-IN')}</Td>
                <Td className="text-right whitespace-nowrap font-mono">
                  {row.pending_invoice_count === 0
                    ? <span className="text-text-secondary">—</span>
                    : <span className="text-charcoal">{row.pending_invoice_count}</span>}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  {canReceive ? (
                    <Button size="sm" onClick={() => onReceive(row)} disabled={receiveDisabled} title={title}>
                      {label}
                    </Button>
                  ) : null}
                </Td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </TableShell>
  )
}
